"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Phone, MapPin, Ruler } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function Action() {
  const shouldReduceMotion = useReducedMotion();
  const trustSignals = [{
    title: "Brookhaven & Dunwoody",
    desc: "Hyper-local focus",
    icon: MapPin
  }, {
    title: "Direct builder access",
    desc: "One point of contact",
    icon: Ruler
  }];
  return <section id="call-to-action" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      {/* Subtle decorative background architectural lines */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="mx-auto h-full max-w-7xl border-x border-border/40" />
      </div>

      <div className="relative mx-auto max-w-6xl px-5 sm:px-8 lg:px-12">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative rounded-2xl border border-border/80 bg-card text-card-foreground p-8 sm:p-12 lg:p-16 shadow-lg overflow-hidden">
          {/* Subtle gold accent divider at card top */}
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-80" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Primary message column */}
            <div className="lg:col-span-7 flex flex-col items-start space-y-6">
              <Badge variant="outline" className="rounded-full border-primary/40 bg-accent/40 text-primary-foreground text-xs tracking-wider uppercase font-semibold px-3.5 py-1">
                In-Home Kitchen Planning
              </Badge>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
                Plan your Brookhaven kitchen renovation.
              </h2>

              <p className="text-base sm:text-lg text-foreground/80 leading-relaxed font-sans max-w-xl">
                Meet in your home to walk through layout possibilities, custom cabinet selections, and a clear budget schedule before work begins.
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2 w-full sm:w-auto">
                <Button asChild size="lg" className="rounded-lg bg-primary text-primary-foreground font-semibold px-8 py-3.5 shadow-sm hover:bg-primary/90 transition-[background-color,box-shadow]">
                  <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center justify-center gap-2">
                    <span>Request an estimate</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="rounded-lg border-border text-foreground hover:bg-accent hover:text-accent-foreground px-6 py-3.5 transition-colors">
                  <a href="tel:+14708479076" className="inline-flex items-center justify-center gap-2">
                    <Phone className="size-4 text-primary" />
                    <span>(470) 847-9076</span>
                  </a>
                </Button>
              </div>

              <div>
                <Link to="#" className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-primary underline underline-offset-4 transition-colors">
                  <span>Read our full kitchen remodeling guide</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </div>

              <div className="flex items-center gap-2 pt-2 text-xs sm:text-sm text-foreground/75">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>No pressure, fixed-scope estimates tailored to Brookhaven home codes</span>
              </div>
            </div>

            {/* Structured credentials panel */}
            <div className="lg:col-span-5">
              <div className="rounded-xl border border-border/70 bg-muted/30 p-6 sm:p-8 space-y-6">
                <div className="border-b border-border/60 pb-4">
                  <h3 className="font-serif text-lg font-medium text-foreground">
                    Project Assurance
                  </h3>
                  <p className="text-xs text-foreground/75 mt-0.5">
                    What to expect from initial feasibility to final installation
                  </p>
                </div>

                <div className="space-y-4">
                  {trustSignals.map((item, i) => {
                  const IconComp = item.icon;
                  return <div key={item.title} data-index={i} className="flex items-start gap-3.5">
                        <div className="size-9 rounded-lg bg-card border border-border flex items-center justify-center text-primary shrink-0 shadow-2xs">
                          <IconComp className="size-4" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">
                            {item.title}
                          </div>
                          <div className="text-xs text-foreground/75">
                            {item.desc}
                          </div>
                        </div>
                      </div>;
                })}
                </div>

                <div className="pt-4 border-t border-border/60 flex items-center justify-between text-xs text-foreground/75">
                  <span>Hours: Mon-Fri: 8am-6pm</span>
                  <Link to="/AboutPage" className="text-foreground hover:text-primary font-medium underline underline-offset-4 transition-colors">Our process</Link>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}